import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import API from '../config/api';

// ─── Reusable admin fetch with JWT ───
function adminFetch(url, method = 'GET', body = null, token = '') {
  const opts = {
    method,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
  };
  if (body) opts.body = JSON.stringify(body);
  return fetch(url, opts).then(r => r.json());
}

// ─── Styles ───
const S = {
  sideHead: { padding: '0 25px', marginBottom: '35px' },
  tabBtn: (active) => ({ display: 'block', width: '100%', padding: '14px 25px', textAlign: 'left', color: active ? '#fff' : '#888', background: active ? 'rgba(200,169,110,0.15)' : 'transparent', borderLeft: active ? '3px solid var(--accent-gold)' : '3px solid transparent', fontSize: '14px', letterSpacing: '0.5px', cursor: 'pointer', borderTop: 'none', borderRight: 'none', borderBottom: 'none', outline: 'none' }),
  topBarClassName: 'admin-topbar-wrapper',
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' },
  card: { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' },
  th: { padding: '14px 18px', textAlign: 'left', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#888', borderBottom: '1px solid #eee', background: '#fafafa' },
  td: { padding: '14px 18px', fontSize: '14px', borderBottom: '1px solid #f5f5f5' },
  addBtn: { padding: '10px 22px', background: 'var(--accent-gold)', color: '#fff', fontSize: '12px', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', borderRadius: '6px' },
  editBtn: { color: 'var(--accent-gold)', cursor: 'pointer', marginRight: '12px', fontSize: '13px', fontWeight: '500' },
  delBtn: { color: '#e74c3c', cursor: 'pointer', fontSize: '13px', fontWeight: '500' },
  // Modal
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: '12px', padding: '35px', width: '900px', maxWidth: '95vw', maxHeight: '95vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  input: { width: '100%', padding: '12px 14px', border: '1.5px solid #e0e0e0', borderRadius: '7px', fontSize: '14px', fontFamily: 'var(--font-sans)', outline: 'none', marginBottom: '16px' },
  label: { display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600', letterSpacing: '0.5px', color: '#555' },
  badge: (color) => ({ display: 'inline-block', padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase', background: color + '18', color }),
};

// ─── Modal Component ───
function Modal({ title, onClose, children }) {
  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
          <h2 style={{ fontSize: '1.3rem', fontWeight: '500' }}>{title}</h2>
          <button onClick={onClose} style={{ fontSize: '22px', color: '#999' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════
// SETTINGS TAB
// ════════════════════════════════════════════
function SettingsTab({ token }) {
  const [loading, setLoading] = useState(true);
  const [heroSaving, setHeroSaving] = useState(false);
  const [topStripSaving, setTopStripSaving] = useState(false);
  const [form, setForm] = useState({
    media_mode: 'slider',
    slide_images: [],
    video_url: '',
    slide_interval_seconds: 6,
    top_strip_enabled: true,
    free_shipping_min_amount: '10000',
    top_strip_offer_text: '',
    top_strip_interval_seconds: 4,
    contact_email: 'support@kaaleenhaus.com',
  });

  const load = () => {
    setLoading(true);
    adminFetch(API.admin.settings, 'GET', null, token)
      .then(d => {
        if (d.status !== 'success' || !d.data) return;
        const slides = Array.isArray(d.data.slide_images) ? d.data.slide_images : [];
        setForm({
          media_mode: d.data.media_mode === 'video' ? 'video' : 'slider',
          slide_images: slides.filter(u => typeof u === 'string' && u.trim()),
          video_url: d.data.video_url || '',
          slide_interval_seconds: Number(d.data.slide_interval_seconds) > 1 ? Number(d.data.slide_interval_seconds) : 6,
          top_strip_enabled: d.data.top_strip_enabled !== false,
          free_shipping_min_amount: String(Number(d.data.free_shipping_min_amount || 10000)),
          top_strip_offer_text: d.data.top_strip_offer_text || '',
          top_strip_interval_seconds: Number(d.data.top_strip_interval_seconds) > 1 ? Number(d.data.top_strip_interval_seconds) : 4,
          contact_email: d.data.contact_email || 'support@kaaleenhaus.com',
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const addSlides = (urls) => {
    const safe = (urls || []).filter(u => typeof u === 'string' && u.trim());
    if (safe.length === 0) return;
    setForm(prev => ({ ...prev, slide_images: [...prev.slide_images, ...safe].slice(0, 8) }));
  };

  const updateSlide = (idx, value) => {
    const next = [...form.slide_images];
    next[idx] = value;
    setForm({ ...form, slide_images: next });
  };

  const removeSlide = (idx) => {
    setForm(prev => ({ ...prev, slide_images: prev.slide_images.filter((_, i) => i !== idx) }));
  };

  const saveHero = () => {
    const cleanSlides = form.slide_images.map(s => (s || '').trim()).filter(Boolean);

    if (form.media_mode === 'slider' && cleanSlides.length < 3) {
      alert('Slider mode needs at least 3 images.');
      return;
    }
    if (form.media_mode === 'video' && !form.video_url.trim()) {
      alert('Video mode needs a video URL.');
      return;
    }

    setHeroSaving(true);
    adminFetch(API.admin.settings, 'PUT', {
      media_mode: form.media_mode,
      slide_images: cleanSlides,
      video_url: form.video_url.trim(),
      slide_interval_seconds: Math.max(2, Math.min(20, Number(form.slide_interval_seconds) || 6)),
    }, token)
      .then(d => {
        if (d.status === 'success') {
          alert('Hero settings saved.');
          load();
          return;
        }
        alert(d.message || 'Failed to save hero settings.');
      })
      .finally(() => setHeroSaving(false));
  };

  const saveTopStrip = () => {
    const emailValue = (form.contact_email || '').trim();
    if (emailValue && !/^\S+@\S+\.\S+$/.test(emailValue)) {
      alert('Please enter a valid contact email.');
      return;
    }

    setTopStripSaving(true);
    adminFetch(API.admin.settings, 'PUT', {
      top_strip_enabled: !!form.top_strip_enabled,
      free_shipping_min_amount: Math.max(0, Number(form.free_shipping_min_amount) || 0),
      top_strip_offer_text: (form.top_strip_offer_text || '').trim(),
      top_strip_interval_seconds: Math.max(2, Math.min(20, Number(form.top_strip_interval_seconds) || 4)),
      contact_email: emailValue,
    }, token)
      .then(d => {
        if (d.status === 'success') {
          alert('Top strip settings saved.');
          load();
          return;
        }
        alert(d.message || 'Failed to save top strip settings.');
      })
      .finally(() => setTopStripSaving(false));
  };

  return (
    <>
      <div style={S.topBar} className={S.topBarClassName}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: '400' }}>Settings</h1>
      </div>

      <div style={{ ...S.card, padding: '24px' }}>
        {loading ? (
          <p style={{ color: '#777' }}>Loading hero settings...</p>
        ) : (
          <>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '18px' }}>
              Homepage Hero Mode: choose an auto-sliding image carousel (with manual arrows) or a single background video.
            </p>

            <label style={S.label}>Hero Media Mode</label>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
              <button
                onClick={() => setForm({ ...form, media_mode: 'slider' })}
                style={{
                  padding: '10px 16px',
                  borderRadius: '7px',
                  border: form.media_mode === 'slider' ? '1.5px solid var(--accent-gold)' : '1.5px solid #ddd',
                  background: form.media_mode === 'slider' ? 'rgba(200,169,110,0.1)' : '#fff',
                  color: '#333',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Slider Images
              </button>
              <button
                onClick={() => setForm({ ...form, media_mode: 'video' })}
                style={{
                  padding: '10px 16px',
                  borderRadius: '7px',
                  border: form.media_mode === 'video' ? '1.5px solid var(--accent-gold)' : '1.5px solid #ddd',
                  background: form.media_mode === 'video' ? 'rgba(200,169,110,0.1)' : '#fff',
                  color: '#333',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Single Video
              </button>
            </div>

            {form.media_mode === 'slider' && (
              <div style={{ border: '1px solid #eee', borderRadius: '10px', padding: '14px', background: '#fafafa' }}>
                <label style={S.label}>Slider Images (recommended 3-4)</label>
                <ImageUpload token={token} value="" onChange={addSlides} label="" multiple={true} />

                <label style={S.label}>Auto Slide Interval (seconds)</label>
                <input
                  style={{ ...S.input, maxWidth: '180px' }}
                  type="number"
                  min="2"
                  max="20"
                  value={form.slide_interval_seconds}
                  onChange={e => setForm({ ...form, slide_interval_seconds: e.target.value })}
                />

                {form.slide_images.length > 0 && (
                  <div style={{ display: 'grid', gap: '10px', marginTop: '8px' }}>
                    {form.slide_images.map((img, idx) => (
                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '130px 1fr auto', gap: '10px', alignItems: 'center' }}>
                        <div style={{ width: '130px', height: '76px', borderRadius: '8px', border: '1px solid #ddd', background: `url(${img}) center/cover` }}></div>
                        <input
                          style={{ ...S.input, marginBottom: 0 }}
                          value={img}
                          onChange={e => updateSlide(idx, e.target.value)}
                          placeholder="https://..."
                        />
                        <button
                          onClick={() => removeSlide(idx)}
                          style={{ color: '#e74c3c', fontSize: '20px', lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {form.media_mode === 'video' && (
              <div style={{ border: '1px solid #eee', borderRadius: '10px', padding: '14px', background: '#fafafa' }}>
                <VideoUpload token={token} value={form.video_url} onChange={val => setForm({ ...form, video_url: val })} label="Hero Video URL (MP4/WebM recommended)" />
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
              <button style={S.addBtn} onClick={saveHero} disabled={heroSaving}>{heroSaving ? 'Saving Hero...' : 'Save Hero Settings'}</button>
            </div>

            <div style={{ marginTop: '22px', borderTop: '1px solid #eee', paddingTop: '18px' }}>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '14px' }}>
                Top Announcement Strip: rotating text shown above navbar on all pages.
              </p>

              <label style={{ ...S.label, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <input
                  type="checkbox"
                  checked={!!form.top_strip_enabled}
                  onChange={e => setForm({ ...form, top_strip_enabled: e.target.checked })}
                />
                Enable Top Announcement Strip
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={S.label}>Free Shipping Minimum Amount (INR)</label>
                  <input
                    style={S.input}
                    type="number"
                    min="0"
                    value={form.free_shipping_min_amount}
                    onChange={e => setForm({ ...form, free_shipping_min_amount: e.target.value })}
                    placeholder="10000"
                  />
                </div>
                <div>
                  <label style={S.label}>Top Strip Slide Interval (seconds)</label>
                  <input
                    style={S.input}
                    type="number"
                    min="2"
                    max="20"
                    value={form.top_strip_interval_seconds}
                    onChange={e => setForm({ ...form, top_strip_interval_seconds: e.target.value })}
                    placeholder="4"
                  />
                </div>
              </div>

              <label style={S.label}>Offer Message (for rotating strip)</label>
              <input
                style={S.input}
                value={form.top_strip_offer_text}
                onChange={e => setForm({ ...form, top_strip_offer_text: e.target.value })}
                placeholder="e.g. NEW ARRIVALS: 15% OFF THIS WEEK"
              />

              <label style={S.label}>Contact Email</label>
              <input
                style={S.input}
                type="email"
                value={form.contact_email}
                onChange={e => setForm({ ...form, contact_email: e.target.value })}
                placeholder="support@kaaleenhaus.com"
              />

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button style={S.addBtn} onClick={saveTopStrip} disabled={topStripSaving}>{topStripSaving ? 'Saving Top Strip...' : 'Save Top Strip Settings'}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ─── Image Upload Component ───
function ImageUpload({ token, value, onChange, label = 'Image URL (or Upload)', multiple = false }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    const uploadedUrls = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('image', file);

      try {
        const uploadUrl = (API.admin && API.admin.upload) ? API.admin.upload : `${window.location.origin}/backend/api/admin/upload.php`;
        const res = await fetch(uploadUrl, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
        const text = await res.text();
        
        let data;
        try { data = JSON.parse(text); } catch(ex) { console.error("Bad JSON", text); continue; }

        if (data.status === 'success') {
          uploadedUrls.push(data.url);
        } else {
          alert(data.message || 'Upload failed');
        }
      } catch (err) {
        console.error("Upload Error:", err);
      }
    }
    
    setUploading(false);
    e.target.value = '';

    if (uploadedUrls.length > 0) {
      if (multiple) {
        onChange(uploadedUrls);
      } else {
        onChange(uploadedUrls[0]);
      }
    }
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      {label && <label style={S.label}>{label}</label>}
      <div style={{ display: 'flex', gap: '8px' }}>
        {!multiple && <input style={{ ...S.input, marginBottom: 0, flex: 1 }} value={value} onChange={e => onChange(e.target.value)} placeholder="https://..." />}
        <label style={{ cursor: uploading ? 'not-allowed' : 'pointer', background: '#f0ede8', padding: '0 15px', borderRadius: '7px', display: 'flex', alignItems: 'center', fontSize: '13px', border: '1.5px solid #e0e0e0', color: uploading ? '#999' : '#333', flex: multiple ? 1 : 'unset', justifyContent: multiple ? 'center' : 'unset' }}>
          {uploading ? 'Uploading...' : (multiple ? '📁 Select Multiple Images' : '📁 Upload')}
          <input type="file" accept="image/*" multiple={multiple} style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
      {!multiple && value && <div style={{ width: '100%', height: '120px', borderRadius: '8px', marginTop: '10px', background: `url(${value}) center/cover`, border: '1px solid #eee' }} />}
    </div>
  );
}

// ─── Video Upload Component ───
function VideoUpload({ token, value, onChange, label = 'Product Video (Optional)' }) {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    let uploadedUrl = '';

    const formData = new FormData();
    formData.append('image', files[0]); // Using the same input name expected by upload.php

    try {
      const uploadUrl = (API.admin && API.admin.upload) ? API.admin.upload : `${window.location.origin}/backend/api/admin/upload.php`;
      const res = await fetch(uploadUrl, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      const text = await res.text();
      
      let data;
      try { data = JSON.parse(text); } catch(ex) { console.error("Bad JSON", text); }

      if (data && data.status === 'success') {
        uploadedUrl = data.url;
      } else {
        alert(data?.message || 'Upload failed');
      }
    } catch (err) {
      console.error("Upload Error:", err);
    }
    
    setUploading(false);
    e.target.value = '';

    if (uploadedUrl) {
      onChange(uploadedUrl);
    }
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      {label && <label style={S.label}>{label}</label>}
      <div style={{ display: 'flex', gap: '8px' }}>
        <input style={{ ...S.input, marginBottom: 0, flex: 1 }} value={value} onChange={e => onChange(e.target.value)} placeholder="https://..." />
        <label style={{ cursor: uploading ? 'not-allowed' : 'pointer', background: '#f0ede8', padding: '0 15px', borderRadius: '7px', display: 'flex', alignItems: 'center', fontSize: '13px', border: '1.5px solid #e0e0e0', color: uploading ? '#999' : '#333' }}>
          {uploading ? 'Uploading...' : '🎬 Upload Video'}
          <input type="file" accept="video/*" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
      {value && <div style={{ width: '100%', borderRadius: '8px', marginTop: '10px', border: '1px solid #eee', overflow: 'hidden' }}><video src={value} controls style={{ width: '100%', maxHeight: '200px', display: 'block', background: '#000' }} /></div>}
    </div>
  );
}

// ─── MultiImageUpload Component ───
function MultiImageUpload({ token, values, onChange }) {
  const arr = typeof values === 'string' ? (values ? JSON.parse(values) : []) : (values || []);
  
  const handleAddMultiple = (urls) => {
    onChange(JSON.stringify([...arr, ...urls]));
  };
  const handleRemove = (i) => {
    onChange(JSON.stringify(arr.filter((_, idx) => idx !== i)));
  };

  return (
    <div style={{ marginBottom: '20px', padding: '15px', background: '#fafafa', border: '1px solid #eee', borderRadius: '8px' }}>
      <label style={{ ...S.label, marginBottom: '12px' }}>Additional Gallery Images</label>
      {arr.length > 0 && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {arr.map((v, i) => (
             <div key={i} style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '6px', background: `url(${v}) center/cover`, border: '1px solid #ddd' }}>
               <button onClick={() => handleRemove(i)} style={{ position: 'absolute', top: '-6px', right: '-6px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '50%', width: '22px', height: '22px', cursor: 'pointer', fontSize: '14px', lineHeight: '22px', textAlign: 'center', padding: 0 }}>×</button>
             </div>
          ))}
        </div>
      )}
      <ImageUpload token={token} value="" onChange={handleAddMultiple} label="" multiple={true} />
    </div>
  );
}

// ─── MultiSelect Component ───
function MultiSelect({ label, options, value, onChange, allowCustom }) {
  const decoded = value ? value.replace(/&amp;/g, '&') : '';
  const activeOpts = decoded ? decoded.split(',').map(s => s.trim()).filter(Boolean) : [];
  const [customVal, setCustomVal] = useState('');
  
  const toggle = (opt) => {
    let newOpts;
    if (activeOpts.includes(opt)) {
      newOpts = activeOpts.filter(o => o !== opt);
    } else {
      newOpts = [...activeOpts, opt];
    }
    onChange(newOpts.join(', '));
  };
  
  const handleAddCustom = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (customVal.trim() && !activeOpts.includes(customVal.trim())) {
        toggle(customVal.trim());
      }
      setCustomVal('');
    }
  };

  const allOpts = [...new Set([...options, ...activeOpts])];

  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{ ...S.label, marginBottom: '6px' }}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
        {allOpts.map(opt => {
          const isActive = activeOpts.includes(opt);
          return (
            <span 
              key={opt}
              onClick={() => toggle(opt)}
              style={{
                cursor: 'pointer',
                padding: '4px 10px',
                borderRadius: '4px',
                fontSize: '11.5px',
                fontWeight: '500',
                border: '1px solid',
                borderColor: isActive ? 'var(--accent-gold)' : '#e0e0e0',
                backgroundColor: isActive ? 'rgba(200,169,110,0.08)' : '#fafafa',
                color: isActive ? 'var(--accent-gold)' : '#666',
                transition: 'all 0.2s',
                userSelect: 'none'
              }}
            >
              {opt}
            </span>
          );
        })}
        {allowCustom && (
          <input 
            type="text" 
            placeholder="+ Custom (Enter)" 
            value={customVal}
            onChange={(e) => setCustomVal(e.target.value)}
            onKeyDown={handleAddCustom}
            style={{
              padding: '4px 10px',
              borderRadius: '4px',
              border: '1px dashed #ccc',
              fontSize: '11.5px',
              outline: 'none',
              width: '100px'
            }}
          />
        )}
      </div>
    </div>
  );
}

// ─── SizePricingField Component ───
function SizePricingField({ label, values, onChange }) {
  let parsed = [];
  try {
    if (values && values.startsWith('[')) parsed = JSON.parse(values);
    else if (typeof values === 'string' && values.length > 0) {
      parsed = values.split(',').map(s => ({ size: s.trim(), b2c: '' }));
    }
  } catch (e) {
    parsed = [];
  }
  
  const handleAdd = () => onChange(JSON.stringify([...parsed, { size: '', b2c: '' }]));
  const handleUpdate = (i, field, val) => {
    const list = [...parsed];
    list[i][field] = val;
    onChange(JSON.stringify(list));
  };
  const handleRemove = (i) => {
    onChange(JSON.stringify(parsed.filter((_, idx) => idx !== i)));
  };

  return (
    <div style={{ marginBottom: '16px', gridColumn: '1 / -1' }}>
      <label style={{ ...S.label, marginBottom: '8px' }}>{label}</label>
      <div style={{ background: '#fafafa', border: '1px solid #eee', padding: '15px', borderRadius: '8px' }}>
        {parsed.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 2fr) 1fr 40px', gap: '10px', marginBottom: '8px', fontSize: '12px', fontWeight: '600', color: '#666' }}>
            <div>Size Name (e.g. 5×8 FT / 152×244 CM)</div>
            <div>Price (₹)</div>
            <div></div>
          </div>
        )}
        
        {parsed.map((item, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 2fr) 1fr 40px', gap: '10px', marginBottom: '8px', alignItems: 'center' }}>
            <input style={{ ...S.input, marginBottom: 0 }} value={item.size} onChange={e => handleUpdate(i, 'size', e.target.value)} placeholder="e.g. 5×8 FT (152×244 CM)" />
            <input style={{ ...S.input, marginBottom: 0 }} type="number" step="0.01" value={item.b2c} onChange={e => handleUpdate(i, 'b2c', e.target.value)} placeholder="Price in ₹" />
            <button onClick={() => handleRemove(i)} style={{ background: '#ffecec', color: '#e74c3c', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>×</button>
          </div>
        ))}
        
        <button onClick={handleAdd} style={{ fontSize: '12.5px', color: 'var(--accent-gold)', fontWeight: '600', marginTop: '10px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>+ Add Size Option</button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════
// CATEGORIES TAB
// ════════════════════════════════════════════
function CategoriesTab({ token }) {
  const [cats, setCats] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', description: '', parent_id: '', image_url: '', discount_percent: '0' });

  const load = () => adminFetch(API.admin.categories, 'GET', null, token).then(d => d.status === 'success' && setCats(d.data));
  useEffect(() => { load(); }, []);

  const parentCats = cats.filter(c => !c.parent_id);
  const getChildren = (pid) => cats.filter(c => c.parent_id == pid);

  const save = () => {
    const method = editing ? 'PUT' : 'POST';
    const body = editing ? { ...form, id: editing.id } : form;
    if (!body.parent_id) delete body.parent_id;
    adminFetch(API.admin.categories, method, body, token).then(d => {
      if (d.status === 'success') { load(); setShowModal(false); setEditing(null); setForm({ name: '', slug: '', description: '', parent_id: '', image_url: '', discount_percent: '0' }); }
      else alert(d.message);
    });
  };

  const del = (id, name) => {
    if (!confirm(`Delete category "${name}"?`)) return;
    adminFetch(API.admin.categories, 'DELETE', { id }, token).then(d => {
      if (d.status === 'success') load(); else alert(d.message);
    });
  };

  const openEdit = (cat) => { setEditing(cat); setForm({ name: cat.name, slug: cat.slug || '', description: cat.description || '', parent_id: cat.parent_id || '', image_url: cat.image_url || '', discount_percent: String(cat.discount_percent ?? 0) }); setShowModal(true); };
  const openAdd = (parentId = '') => { setEditing(null); setForm({ name: '', slug: '', description: '', parent_id: parentId, image_url: '', discount_percent: '0' }); setShowModal(true); };

  return (
    <>
      <div style={S.topBar} className={S.topBarClassName}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: '400' }}>Categories <span style={{ fontSize: '14px', color: '#888', fontWeight: '400' }}>({cats.length})</span></h1>
        <button style={S.addBtn} onClick={() => openAdd()}>+ Add Category</button>
      </div>
      <div style={S.card}>
      <div className="admin-table-scroll">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '500px' }}>
          <thead><tr>
            <th style={S.th}>ID</th><th style={S.th}>Name</th><th style={S.th}>Parent</th><th style={S.th}>Slug</th><th style={S.th}>Discount</th><th style={S.th}>Products</th><th style={S.th}>Actions</th>
          </tr></thead>
          <tbody>
            {cats.length > 0 ? cats.map(c => (
              <tr key={c.id} style={{ background: c.parent_id ? '#fafafa' : '#fff' }}>
                <td style={S.td}>{c.id}</td>
                <td style={{ ...S.td, fontWeight: '500', paddingLeft: c.parent_id ? '35px' : '18px' }}>{c.parent_id && '↳ '}{c.name}</td>
                <td style={{ ...S.td, color: '#888', fontSize: '12px' }}>{c.parent_id ? (parentCats.find(p => p.id == c.parent_id)?.name || '—') : '—'}</td>
                <td style={{ ...S.td, color: '#888' }}>{c.slug}</td>
                <td style={S.td}><span style={S.badge('#ef4444')}>{Number(c.discount_percent || 0)}%</span></td>
                <td style={S.td}><span style={S.badge('#3b82f6')}>{c.product_count}</span></td>
                <td style={S.td}>
                  {!c.parent_id && <span style={{ ...S.editBtn, color: '#10b981' }} onClick={() => openAdd(c.id)}>+ Sub</span>}
                  <span style={S.editBtn} onClick={() => openEdit(c)}>Edit</span>
                  <span style={S.delBtn} onClick={() => del(c.id, c.name)}>Delete</span>
                </td>
              </tr>
            )) : <tr><td colSpan="7" style={{ ...S.td, textAlign: 'center', color: '#999', padding: '40px' }}>No categories created.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      {showModal && (
        <Modal title={editing ? 'Edit Category' : (form.parent_id ? 'Add Sub-Category' : 'Add Category')} onClose={() => setShowModal(false)}>
          <label style={S.label}>Category Name *</label>
          <input style={S.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Persian Rugs" />
          
          <label style={S.label}>Custom Slug (optional)</label>
          <input style={S.input} value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="e.g. persian-rugs (auto-generated if empty)" />
          
          <label style={S.label}>Parent Category (leave empty for top-level)</label>
          <select style={S.input} value={form.parent_id} onChange={e => setForm({ ...form, parent_id: e.target.value })}>
            <option value="">— None (Top Level) —</option>
            {parentCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <ImageUpload token={token} value={form.image_url} onChange={val => setForm({ ...form, image_url: val })} />

          <label style={S.label}>Category Discount (%)</label>
          <input
            style={S.input}
            type="number"
            min="0"
            max="95"
            step="0.01"
            value={form.discount_percent}
            onChange={e => setForm({ ...form, discount_percent: e.target.value })}
            placeholder="0"
          />
          
          <label style={S.label}>Description</label>
          <textarea style={{ ...S.input, minHeight: '80px', resize: 'vertical' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description..." />
          <button style={{ ...S.addBtn, width: '100%', padding: '14px' }} onClick={save}>{editing ? 'Update Category' : 'Create Category'}</button>
        </Modal>
      )}
    </>
  );
}

// ════════════════════════════════════════════
// PRODUCTS TAB
// ════════════════════════════════════════════

const FILTER_OPTS = {
  sizes: ['2×3 FT (61×91 CM)', '3×5 FT (91×152 CM)', '4×6 FT (122×183 CM)', '5×8 FT (152×244 CM)', '6×9 FT (183×274 CM)', '8×10 FT (244×305 CM)', '9×12 FT (274×366 CM)', '10×14 FT (305×427 CM)', '12×15 FT (366×457 CM)', 'Small', 'Medium', 'Large', 'Oversize'],
  colors: ['Blue', 'Red', 'Green', 'Yellow', 'Ivory / White', 'Grey', 'Black', 'Orange', 'Pink', 'Purple', 'Brown', 'Beige', 'Multi Color'],
  rooms: ['Living Room', 'Dining Room', 'Bedroom', 'Kids Room', 'Outdoor/Indoor'],
  materials: ['Wool', 'Wool & Bamboo Silk', 'Wool & Silk', 'Silk', 'Viscose', 'Jute & Hemp', 'Cotton', 'Polyester'],
  constructions: ['Hand Knotted', 'Hand Tufted', 'Hand Loom', 'Flat Weaves', 'Shag', 'Wall Décor'],
  shapes: ['Rectangle', 'Irregular', 'Round', 'Runner', 'Oval', 'Square']
};

function ProductsTab({ token }) {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', category_id: '', delivery_price: '500', delivery_type: 'stock', dispatch_min_days: '1', dispatch_max_days: '3', stock_quantity: '', image_url: '', additional_images: '[]', sizes: '', size_variants: '', colors: '', rooms: '', materials: '', constructions: '', shapes: '', seo_keywords: '', pile_height: '' });

  const load = () => {
    adminFetch(API.admin.products, 'GET', null, token).then(d => d.status === 'success' && setProducts(d.data));
    adminFetch(API.admin.categories, 'GET', null, token).then(d => d.status === 'success' && setCats(d.data));
  };
  useEffect(() => { load(); }, []);

  const save = () => {
    if (!form.name) return alert('Product name is required.');
    const deliveryType = form.delivery_type === 'made_to_order' ? 'made_to_order' : 'stock';
    const fallbackMin = deliveryType === 'made_to_order' ? '15' : '1';
    const fallbackMax = deliveryType === 'made_to_order' ? '20' : '3';
    const minDays = String(form.dispatch_min_days || fallbackMin);
    const maxDays = String(form.dispatch_max_days || fallbackMax);
    const safeMin = Math.max(0, Number(minDays));
    const safeMax = Math.max(safeMin, Number(maxDays));
    const finalForm = {
      ...form,
      b2c_price: '0',
      b2b_price: '0',
      delivery_price: form.delivery_price || '500',
      delivery_type: deliveryType,
      dispatch_min_days: String(safeMin),
      dispatch_max_days: String(safeMax),
      stock_quantity: deliveryType === 'stock' ? (form.stock_quantity === '' ? '' : String(Math.max(0, Number(form.stock_quantity)))) : ''
    };
    const method = editing ? 'PUT' : 'POST';
    const body = editing ? { ...finalForm, id: editing.id } : finalForm;
    adminFetch(API.admin.products, method, body, token).then(d => {
      if (d.status === 'success') { load(); setShowModal(false); setEditing(null); }
      else alert(d.message);
    });
  };

  const del = (id, name) => {
    if (!confirm(`Delete product "${name}"? This cannot be undone.`)) return;
    adminFetch(API.admin.products, 'DELETE', { id }, token).then(d => {
      if (d.status === 'success') load(); else alert(d.message);
    });
  };

  const openEdit = (p) => {
    setEditing(p);
    // If sizes contains JSON (old format), clear it so MultiSelect works
    let cleanSizes = p.sizes || '';
    if (cleanSizes.startsWith('[')) cleanSizes = '';
    const deliveryType = (p.delivery_type || '').toLowerCase() === 'made_to_order' ? 'made_to_order' : 'stock';
    const minDays = String(p.dispatch_min_days ?? (deliveryType === 'made_to_order' ? 15 : 1));
    const maxDays = String(p.dispatch_max_days ?? (deliveryType === 'made_to_order' ? 20 : 3));
    setForm({ name: p.name, description: p.description || '', category_id: p.category_id || '', delivery_price: p.delivery_price || '500', delivery_type: deliveryType, dispatch_min_days: minDays, dispatch_max_days: maxDays, stock_quantity: p.stock_quantity ?? '', image_url: p.image_url || '', video_url: p.video_url || '', additional_images: p.additional_images || '[]', sizes: cleanSizes, size_variants: p.size_variants || '', colors: (p.colors || '').replace(/&amp;/g, '&'), rooms: (p.rooms || '').replace(/&amp;/g, '&'), materials: (p.materials || '').replace(/&amp;/g, '&'), constructions: (p.constructions || '').replace(/&amp;/g, '&'), shapes: (p.shapes || '').replace(/&amp;/g, '&'), seo_keywords: p.seo_keywords || '', pile_height: p.pile_height || '' });
    setShowModal(true);
  };
  const openAdd = () => { setEditing(null); setForm({ name: '', description: '', category_id: '', delivery_price: '500', delivery_type: 'stock', dispatch_min_days: '1', dispatch_max_days: '3', stock_quantity: '', image_url: '', video_url: '', additional_images: '[]', sizes: '', size_variants: '', colors: '', rooms: '', materials: '', constructions: '', shapes: '', seo_keywords: '', pile_height: '' }); setShowModal(true); };

  return (
    <>
      <div style={S.topBar} className={S.topBarClassName}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: '400' }}>Products <span style={{ fontSize: '14px', color: '#888', fontWeight: '400' }}>({products.length})</span></h1>
        <button style={S.addBtn} onClick={openAdd}>+ Add Product</button>
      </div>
      <div style={S.card}>
        <div className="admin-table-scroll">
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
          <thead><tr>
            <th style={S.th}>ID / SKU</th><th style={S.th}>Image</th><th style={S.th}>Name</th><th style={S.th}>Category</th><th style={S.th}>Price (per size)</th><th style={S.th}>Delivery (Charge + ETA)</th><th style={S.th}>Stock</th><th style={S.th}>Actions</th>
          </tr></thead>
          <tbody>
            {products.length > 0 ? products.map(p => (
              <tr key={p.id}>
                <td style={S.td}>
                  <div style={{ fontWeight: '500' }}>#{p.id}</div>
                  <div style={{ fontSize: '11px', color: '#888', marginTop: '2px', letterSpacing: '0.5px' }}>{p.sku || 'NO SKU'}</div>
                </td>
                <td style={S.td}>
                  <div style={{ width: '45px', height: '45px', borderRadius: '6px', background: p.image_url ? `url(${p.image_url}) center/cover` : '#f0ede8' }} />
                </td>
                <td style={{ ...S.td, fontWeight: '500' }}>{p.sku || p.name}</td>
                <td style={S.td}><span style={S.badge('#8b5cf6')}>{p.category_name || '—'}</span></td>
                <td style={S.td}>{(() => { try { const sv = JSON.parse(p.size_variants || '[]'); return sv.length > 0 ? `₹${sv[0].b2c} +${sv.length > 1 ? (sv.length - 1) + ' more' : ''}` : '—'; } catch(e) { return '—'; } })()}</td>
                <td style={S.td}>
                  <div>₹{Number(p.delivery_price || 0).toFixed(2)}</div>
                  <div style={{ fontSize: '11px', color: '#777', marginTop: '2px' }}>
                    {(p.delivery_type === 'made_to_order' ? 'Made to order' : 'In stock')} · {Number(p.dispatch_min_days ?? 1)}-{Number(p.dispatch_max_days ?? 3)} days
                  </div>
                </td>
                <td style={S.td}>
                  {p.delivery_type === 'made_to_order'
                    ? <span style={S.badge('#7c3aed')}>MTO</span>
                    : (p.stock_quantity === null || p.stock_quantity === undefined || p.stock_quantity === ''
                      ? <span style={S.badge('#6b7280')}>Untracked</span>
                      : <span style={S.badge(Number(p.stock_quantity) <= 5 ? '#ef4444' : '#10b981')}>{Number(p.stock_quantity)} pcs</span>)}
                </td>
                <td style={S.td}>
                  <span style={S.editBtn} onClick={() => openEdit(p)}>Edit</span>
                  <span style={S.delBtn} onClick={() => del(p.id, p.name)}>Delete</span>
                </td>
              </tr>
            )) : <tr><td colSpan="8" style={{ ...S.td, textAlign: 'center', color: '#999', padding: '40px' }}>No products yet. Add your first product!</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      {showModal && (
        <Modal title={editing ? 'Edit Product' : 'Add Product'} onClose={() => setShowModal(false)}>
          <label style={S.label}>Product Name *</label>
          <input style={S.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Persian Royal Heritage" />

          <label style={S.label}>Category</label>
          <select style={S.input} value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
            <option value="">— Select —</option>
            {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={S.label}>Delivery Price (INR)</label>
              <input style={S.input} type="number" step="0.01" min="0" value={form.delivery_price} onChange={e => setForm({ ...form, delivery_price: e.target.value })} placeholder="0.00" />
            </div>
            <div>
              <label style={S.label}>Pile Height / Thickness</label>
              <input style={S.input} value={form.pile_height} onChange={e => setForm({ ...form, pile_height: e.target.value })} placeholder="e.g. 12 mm, 0.5 inch, Low Pile" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div>
              <label style={S.label}>Fulfillment Type</label>
              <select
                style={S.input}
                value={form.delivery_type}
                onChange={e => {
                  const nextType = e.target.value === 'made_to_order' ? 'made_to_order' : 'stock';
                  const defaults = nextType === 'made_to_order'
                    ? { dispatch_min_days: '15', dispatch_max_days: '20' }
                    : { dispatch_min_days: '1', dispatch_max_days: '3' };
                  setForm({ ...form, delivery_type: nextType, ...defaults, stock_quantity: nextType === 'made_to_order' ? '' : form.stock_quantity });
                }}
              >
                <option value="stock">In Stock</option>
                <option value="made_to_order">Made To Order</option>
              </select>
            </div>
            <div>
              <label style={S.label}>Dispatch Min Days</label>
              <input
                style={S.input}
                type="number"
                min="0"
                value={form.dispatch_min_days}
                onChange={e => setForm({ ...form, dispatch_min_days: e.target.value })}
                placeholder={form.delivery_type === 'made_to_order' ? '15' : '1'}
              />
            </div>
            <div>
              <label style={S.label}>Dispatch Max Days</label>
              <input
                style={S.input}
                type="number"
                min="0"
                value={form.dispatch_max_days}
                onChange={e => setForm({ ...form, dispatch_max_days: e.target.value })}
                placeholder={form.delivery_type === 'made_to_order' ? '20' : '3'}
              />
            </div>
          </div>

          {form.delivery_type === 'stock' && (
            <div>
              <label style={S.label}>Stock Quantity (leave blank for untracked)</label>
              <input
                style={{ ...S.input, maxWidth: '240px' }}
                type="number"
                min="0"
                step="1"
                value={form.stock_quantity}
                onChange={e => setForm({ ...form, stock_quantity: e.target.value })}
                placeholder="e.g. 25"
              />
            </div>
          )}

          <ImageUpload token={token} value={form.image_url} onChange={val => setForm({ ...form, image_url: val })} label="Main Product Image (Thumbnail & Hero)" />
          <VideoUpload token={token} value={form.video_url} onChange={val => setForm({ ...form, video_url: val })} />
          <MultiImageUpload token={token} values={form.additional_images} onChange={val => setForm({ ...form, additional_images: val })} />

          <label style={S.label}>Description</label>
          <textarea style={{ ...S.input, minHeight: '80px', resize: 'vertical' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Detailed product description..." />

          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1b1c15', marginTop: '10px', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>SEO & Search</h3>
          <div style={{ marginBottom: '16px' }}>
            <label style={S.label}>SEO Keywords / Tags <span style={{ fontWeight: '400', color: '#999' }}>(comma-separated, helps Google find your product)</span></label>
            <textarea style={{ ...S.input, minHeight: '60px', resize: 'vertical' }} value={form.seo_keywords} onChange={e => setForm({ ...form, seo_keywords: e.target.value })} placeholder="e.g. handmade rug, Persian carpet, wool rug Germany, luxury floor covering, Teppich handgeknüpft" />
            <p style={{ fontSize: '11px', color: '#999', marginTop: '-10px', marginBottom: '12px' }}>💡 Add keywords buyers might search on Google. Include German terms for European SEO.</p>
          </div>

          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1b1c15', marginTop: '10px', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '8px' }}>Product Attributes (Mega Menu Filters)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', marginBottom: '16px' }}>
            <MultiSelect label="Sizes (with CM)" options={FILTER_OPTS.sizes} value={form.sizes} onChange={v => setForm({ ...form, sizes: v })} />
            <SizePricingField label="Size & Pricing Variants" values={form.size_variants} onChange={v => setForm({ ...form, size_variants: v })} />
            <MultiSelect label="Colors" options={FILTER_OPTS.colors} value={form.colors} onChange={v => setForm({ ...form, colors: v })} />
            <MultiSelect label="Rooms" options={FILTER_OPTS.rooms} value={form.rooms} onChange={v => setForm({ ...form, rooms: v })} />
            <MultiSelect label="Materials" options={FILTER_OPTS.materials} value={form.materials} onChange={v => setForm({ ...form, materials: v })} allowCustom />
            <MultiSelect label="Constructions" options={FILTER_OPTS.constructions} value={form.constructions} onChange={v => setForm({ ...form, constructions: v })} />
            <MultiSelect label="Shapes" options={FILTER_OPTS.shapes} value={form.shapes} onChange={v => setForm({ ...form, shapes: v })} />
          </div>

          <button style={{ ...S.addBtn, width: '100%', padding: '14px' }} onClick={save}>{editing ? 'Update Product' : 'Create Product'}</button>
        </Modal>
      )}
    </>
  );
}

// ════════════════════════════════════════════
// ORDERS TAB
// ════════════════════════════════════════════
function OrdersTab({ token }) {
  const [orders, setOrders] = useState([]);
  const load = () => adminFetch(API.admin.orders, 'GET', null, token).then(d => d.status === 'success' && setOrders(d.data));
  useEffect(() => { load(); }, []);

  const statusColors = { pending: '#f59e0b', paid: '#3b82f6', shipped: '#8b5cf6', delivered: '#10b981', cancelled: '#ef4444', failed: '#ef4444' };

  const updateStatus = (id, status) => {
    const body = { id, status };

    if (status === 'shipped') {
      const targetOrder = orders.find(o => Number(o.id) === Number(id));
      const existingTracking = targetOrder?.tracking_number || '';
      const trackingInput = window.prompt('Enter tracking ID before marking this order as shipped.', existingTracking);
      if (trackingInput === null) {
        return;
      }

      const trackingNumber = trackingInput.trim();
      if (!trackingNumber) {
        alert('Tracking ID is required for shipped orders.');
        return;
      }

      body.tracking_number = trackingNumber;
    }

    adminFetch(API.admin.orders, 'PUT', body, token).then(d => {
      if (d.status === 'success') load(); else alert(d.message);
    });
  };

  return (
    <>
      <div style={S.topBar} className={S.topBarClassName}><h1 style={{ fontSize: '1.6rem', fontWeight: '400' }}>Orders <span style={{ fontSize: '14px', color: '#888' }}>({orders.length})</span></h1></div>
      <div style={S.card}>
        <div className="admin-table-scroll">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
          <thead><tr>
            <th style={S.th}>ID</th><th style={S.th}>Customer</th><th style={S.th}>Type</th><th style={S.th}>Items</th><th style={S.th}>Total</th><th style={S.th}>Tracking</th><th style={S.th}>Status</th><th style={S.th}>Date</th>
          </tr></thead>
          <tbody>
            {orders.length > 0 ? orders.map(o => (
              <tr key={o.id}>
                <td style={S.td}>#{o.id}</td>
                <td style={S.td}><div style={{ fontWeight: '500' }}>{o.customer_name}</div><div style={{ fontSize: '12px', color: '#888' }}>{o.customer_email}</div></td>
                <td style={S.td}><span style={S.badge('#8b5cf6')}>{o.buyer_type}</span></td>
                <td style={S.td}>{o.item_count}</td>
                <td style={{ ...S.td, fontWeight: '500' }}>₹{o.total_amount}</td>
                <td style={{ ...S.td, fontSize: '12px', color: o.tracking_number ? '#111' : '#999' }}>{o.tracking_number || '—'}</td>
                <td style={S.td}>
                  <select value={o.status} onChange={e => updateStatus(o.id, e.target.value)} style={{ padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '12px', fontWeight: '600', color: statusColors[o.status] || '#888', background: '#fff', cursor: 'pointer' }}>
                    {['pending', 'paid', 'shipped', 'delivered', 'cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </td>
                <td style={{ ...S.td, color: '#888', fontSize: '13px' }}>{new Date(o.created_at).toLocaleDateString()}</td>
              </tr>
              )) : <tr><td colSpan="8" style={{ ...S.td, textAlign: 'center', color: '#999', padding: '40px' }}>No orders found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════
// USERS TAB
// ════════════════════════════════════════════
function UsersTab({ token }) {
  const [users, setUsers] = useState([]);
  const load = () => adminFetch(API.admin.users, 'GET', null, token).then(d => d.status === 'success' && setUsers(d.data));
  useEffect(() => { load(); }, []);

  const roleColors = { admin: '#ef4444', wholesale: '#8b5cf6', retail: '#10b981' };

  const updateRole = (id, buyer_type) => {
    adminFetch(API.admin.users, 'PUT', { id, buyer_type }, token).then(d => {
      if (d.status === 'success') load(); else alert(d.message);
    });
  };

  return (
    <>
      <div style={S.topBar} className={S.topBarClassName}><h1 style={{ fontSize: '1.6rem', fontWeight: '400' }}>Users <span style={{ fontSize: '14px', color: '#888' }}>({users.length})</span></h1></div>
      <div style={S.card}>
        <div className="admin-table-scroll">
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
          <thead><tr>
            <th style={S.th}>ID</th><th style={S.th}>Name</th><th style={S.th}>Email</th><th style={S.th}>Phone</th><th style={S.th}>Role</th><th style={S.th}>Joined</th>
          </tr></thead>
          <tbody>
            {users.length > 0 ? users.map(u => (
              <tr key={u.id}>
                <td style={S.td}>{u.id}</td>
                <td style={{ ...S.td, fontWeight: '500' }}>{u.name}</td>
                <td style={{ ...S.td, color: '#555' }}>{u.email}</td>
                <td style={{ ...S.td, color: '#888' }}>{u.phone_number || '—'}</td>
                <td style={S.td}>
                  <select value={u.buyer_type} onChange={e => updateRole(u.id, e.target.value)} style={{ padding: '6px 10px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '12px', fontWeight: '600', color: roleColors[u.buyer_type] || '#888', background: '#fff', cursor: 'pointer' }}>
                    <option value="retail">Retail</option>
                    <option value="wholesale">Wholesale</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td style={{ ...S.td, color: '#888', fontSize: '13px' }}>{new Date(u.created_at).toLocaleDateString()}</td>
              </tr>
            )) : <tr><td colSpan="6" style={{ ...S.td, textAlign: 'center', color: '#999', padding: '40px' }}>No users registered yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════
// NAVIGATION TAB
// ════════════════════════════════════════════
function NavigationTab({ token }) {
  const [sections, setSections] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ menu_key: 'shop', column_title: '', items: [{ label: '', link: '' }], sort_order: 0, image_url: '' });

  const load = () => adminFetch(API.admin.navSections, 'GET', null, token).then(d => d.status === 'success' && setSections(d.data));
  useEffect(() => { load(); }, []);

  const menuKeys = ['shop', 'style', 'stories', 'blog'];
  const menuColors = { shop: '#3b82f6', style: '#8b5cf6', stories: '#f59e0b', blog: '#10b981' };

  const addItem = () => setForm({ ...form, items: [...form.items, { label: '', link: '' }] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const updateItem = (i, field, val) => { const items = [...form.items]; items[i][field] = val; setForm({ ...form, items }); };

  const save = () => {
    if (!form.column_title || form.items.length === 0) return alert('Title and at least one item required.');
    const cleanItems = form.items.filter(i => i.label.trim());
    if (cleanItems.length === 0) return alert('Add at least one item with a label.');
    const method = editing ? 'PUT' : 'POST';
    const body = { ...form, items: cleanItems };
    if (editing) body.id = editing.id;
    adminFetch(API.admin.navSections, method, body, token).then(d => {
      if (d.status === 'success') { load(); setShowModal(false); setEditing(null); }
      else alert(d.message);
    });
  };

  const del = (id, title) => {
    if (!confirm(`Delete nav section "${title}"?`)) return;
    adminFetch(API.admin.navSections, 'DELETE', { id }, token).then(d => {
      if (d.status === 'success') load(); else alert(d.message);
    });
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ menu_key: s.menu_key, column_title: s.column_title, items: typeof s.items === 'string' ? JSON.parse(s.items) : s.items, sort_order: s.sort_order || 0, image_url: s.image_url || '' });
    setShowModal(true);
  };
  const openAdd = () => { setEditing(null); setForm({ menu_key: 'shop', column_title: '', items: [{ label: '', link: '' }], sort_order: 0, image_url: '' }); setShowModal(true); };

  return (
    <>
      <div style={S.topBar}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: '400' }}>Navigation Menu</h1>
        <button style={S.addBtn} onClick={openAdd}>+ Add Section</button>
      </div>
      <p style={{ fontSize: '13px', color: '#888', marginBottom: '20px' }}>Manage the mega menu dropdowns. Each section is a column under a nav item (Shop, Style, Stories, Blog).</p>

      {/* Group by menu_key */}
      {menuKeys.map(key => {
        const keyItems = sections.filter(s => s.menu_key === key);
        if (keyItems.length === 0) return (
          <div key={key} style={{ ...S.card, padding: '25px', marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={S.badge(menuColors[key])}>{key.toUpperCase()}</span>
              <span style={{ marginLeft: '12px', color: '#999', fontSize: '13px' }}>No sections added yet</span>
            </div>
            <button onClick={() => { setEditing(null); setForm({ menu_key: key, column_title: '', items: [{ label: '', link: '' }], sort_order: 0, image_url: '' }); setShowModal(true); }} style={{ fontSize: '12px', color: 'var(--accent-gold)', fontWeight: '600' }}>+ Add to {key}</button>
          </div>
        );
        return (
          <div key={key} style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <span style={S.badge(menuColors[key])}>{key.toUpperCase()}</span>
              <span style={{ color: '#888', fontSize: '12px' }}>{keyItems.length} column(s)</span>
            </div>
            <div style={S.card}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={S.th}>Column Title</th><th style={S.th}>Items</th><th style={S.th}>Order</th><th style={S.th}>Image</th><th style={S.th}>Actions</th>
                </tr></thead>
                <tbody>
                  {keyItems.map(s => {
                    const items = typeof s.items === 'string' ? JSON.parse(s.items) : s.items;
                    return (
                      <tr key={s.id}>
                        <td style={{ ...S.td, fontWeight: '500' }}>{s.column_title}</td>
                        <td style={{ ...S.td, fontSize: '12px', color: '#666' }}>{items.map(i => i.label).join(', ')}</td>
                        <td style={S.td}>{s.sort_order}</td>
                        <td style={S.td}>{s.image_url ? '✅' : '—'}</td>
                        <td style={S.td}>
                          <span style={S.editBtn} onClick={() => openEdit(s)}>Edit</span>
                          <span style={S.delBtn} onClick={() => del(s.id, s.column_title)}>Delete</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {showModal && (
        <Modal title={editing ? 'Edit Nav Section' : 'Add Nav Section'} onClose={() => setShowModal(false)}>
          <label style={S.label}>Menu Dropdown *</label>
          <select style={S.input} value={form.menu_key} onChange={e => setForm({ ...form, menu_key: e.target.value })}>
            {menuKeys.map(k => <option key={k} value={k}>{k.charAt(0).toUpperCase() + k.slice(1)}</option>)}
          </select>

          <label style={S.label}>Column Title *</label>
          <input style={S.input} value={form.column_title} onChange={e => setForm({ ...form, column_title: e.target.value })} placeholder="e.g. Room, Material, Construction" />

          <label style={S.label}>Sort Order</label>
          <input style={S.input} type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />

          <ImageUpload token={token} value={form.image_url} onChange={val => setForm({ ...form, image_url: val })} />

          <label style={S.label}>Items</label>
          {form.items.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <input style={{ ...S.input, flex: 1, marginBottom: 0 }} value={item.label} onChange={e => updateItem(i, 'label', e.target.value)} placeholder="Label (e.g. Living Room)" />
              <input style={{ ...S.input, flex: 1, marginBottom: 0 }} value={item.link} onChange={e => updateItem(i, 'link', e.target.value)} placeholder="Link (e.g. /shop?room=living)" />
              {form.items.length > 1 && <button onClick={() => removeItem(i)} style={{ color: '#e74c3c', fontSize: '18px', flexShrink: 0 }}>×</button>}
            </div>
          ))}
          <button onClick={addItem} style={{ fontSize: '12px', color: 'var(--accent-gold)', fontWeight: '600', marginBottom: '20px' }}>+ Add Item</button>

          <button style={{ ...S.addBtn, width: '100%', padding: '14px' }} onClick={save}>{editing ? 'Update Section' : 'Create Section'}</button>
        </Modal>
      )}
    </>
  );
}

// ════════════════════════════════════════════
// MAIN ADMIN LAYOUT
// ════════════════════════════════════════════
export default function Admin() {
  const { user, token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('products');
  const [menuOpen, setMenuOpen] = useState(false);

  const tabs = [
    { key: 'products', label: '📦 Products' },
    { key: 'categories', label: '🏷️ Categories' },
    { key: 'navigation', label: '🧭 Navigation' },
    { key: 'settings', label: '⚙️ Settings' },
    { key: 'orders', label: '📋 Orders' },
    { key: 'users', label: '👥 Users' },
  ];

  return (
    <div className="admin-layout">
      <style>{`
        .admin-layout { display: flex; min-height: calc(100vh - 120px); position: relative; }
        .admin-sidebar { 
          width: 260px; 
          background: var(--bg-dark); 
          color: #fff; 
          padding: 30px 0; 
          flex-shrink: 0;
          transition: transform 0.3s ease;
          position: sticky;
          top: 0;
          height: calc(100vh - 120px);
          z-index: 100;
        }
        .admin-main { flex: 1; padding: 30px 40px; background: #f5f5f7; overflow-x: hidden; width: 100%; min-width: 0; }
        .admin-hamburger { display: none; background: transparent; border: none; font-size: 24px; cursor: pointer; padding: 0 10px 15px 0; color: #111; align-items: center; gap: 8px; }
        .admin-overlay { display: none; }
        
        .admin-table-scroll { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }

        @media (max-width: 768px) {
          .admin-sidebar {
            position: fixed;
            top: 0; left: 0; bottom: 0;
            height: 100vh;
            transform: translateX(-100%);
            z-index: 1000;
          }
          .admin-sidebar.open {
            transform: translateX(0);
          }
          .admin-hamburger {
            display: flex;
          }
          .admin-overlay.open {
            display: block; position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 999;
          }
          .admin-main { padding: 20px 15px; }
          .admin-topbar-wrapper { flex-direction: column; align-items: flex-start !important; gap: 15px; display: flex; }
          .admin-topbar-wrapper h1 { font-size: 1.4rem !important; }
        }
      `}</style>

      <div className={`admin-overlay ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(false)}></div>

      <aside className={`admin-sidebar ${menuOpen ? 'open' : ''}`}>
        <div style={S.sideHead}>
          <h2 style={{ fontSize: '1.2rem', fontWeight: '500', letterSpacing: '1px' }}>Admin Panel</h2>
          <p style={{ fontSize: '13px', color: 'var(--accent-gold)', marginTop: '8px', letterSpacing: '0.5px' }}>Welcome, {user?.name}</p>
        </div>
        <nav>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => { setActiveTab(tab.key); setMenuOpen(false); }} style={S.tabBtn(activeTab === tab.key)}>
              {tab.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: '25px', marginTop: 'auto', borderTop: '1px solid rgba(255,255,255,0.08)', fontSize: '11px', color: '#666', bottom: '20px', width: '100%', position: 'absolute' }}>
          Kaaleen Haus v1.0
        </div>
      </aside>

      <main className="admin-main">
        <button className="admin-hamburger" onClick={() => setMenuOpen(true)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
        </button>
        {activeTab === 'products' && <ProductsTab token={token} />}
        {activeTab === 'categories' && <CategoriesTab token={token} />}
        {activeTab === 'navigation' && <NavigationTab token={token} />}
        {activeTab === 'settings' && <SettingsTab token={token} />}
        {activeTab === 'orders' && <OrdersTab token={token} />}
        {activeTab === 'users' && <UsersTab token={token} />}
      </main>
    </div>
  );
}
